import {
  CopyIcon,
  GearIcon,
  Link2Icon,
  Pencil1Icon,
  TrashIcon,
  ClipboardIcon,
  ClipboardCopyIcon,
  GridIcon,
  EyeClosedIcon,
  EyeOpenIcon,
  Cross2Icon,
  DownloadIcon,
} from '@radix-ui/react-icons'
import { Replicache } from 'replicache'
import { toast } from 'sonner'
import {
  Block,
  blockTypesRecord,
  isCommentBlock,
  isComponentBlock,
  isDocumentBlock,
  isInstanceBlock,
  isListBlock,
  isMediaBlock,
  isTextBlock,
  isRichTextBlock,
  isHidableBlock,
  shouldInheritHiddenState,
  isReferenceBlock,
  CommonBlocksCompoundIndexes,
  getValueBasedOnSelectedVariants,
  VariantData,
} from '@basehub/data/replicache/index'
import { repoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import { getId } from '@basehub/utils/id/index'
import { TabsStore } from '../../tabs/store'
import { MenuItem } from '~/sotano-kit/primitives/menu-actions-definitions'
import { invariant } from '@epic-web/invariant'
import { TreeManager } from '@basehub/utils/tree/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { copyBlockToClipboard } from '~/hooks-data/copy-paste'
import { ListBlockViewIcon } from '../list/views/view-switcher'
import { RepoStore } from '../../repo-store'
import { EditorState } from '../../editor-store'
import { replaceAssetsURL } from '@basehub/utils/image-utils'
import { BlockIcon } from '~/components/block-icon'
import {
  enhanceGeneratedHTML,
  jsonToPlainText,
  richTextExtensionsForTransformations,
  stateToJson,
  tipTapJSONToHTMLString,
} from '@basehub/tiptap-api-utils'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import rehypeParse from 'rehype-parse'
import rehypeRemark from 'rehype-remark'
import remarkGfm from 'remark-gfm'
import remarkStringify from 'remark-stringify'
import { unified } from 'unified'
import { EditorMode } from '../../editor-mode-provider'
import {
  RepoUndoManager,
  RepoUndoOps,
} from '~/replicache/frontend/undo-redo/manager'
import {
  DuplicateBlockParams,
  UpdateBlockParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { ChangeBlockTypeParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/change'
import { sleep } from '~/lib/sleep'

type BaseParams = {
  teamSlug: string
  repoSlug: string
  branchOrCommitSegment: string | undefined
  parentBlockId: string
  blocks: Block[]
  parentBlock: Block
  repoStore: Pick<RepoStore, 'setFocus'>
  tabsStore: TabsStore
  treeManagerRef: React.MutableRefObject<TreeManager<
    Block,
    CommonBlocksCompoundIndexes
  > | null>
  undoManager: RepoUndoManager | null
  selectedVariants: VariantData[]
}

type GetBlockMenuActionsParams =
  | (BaseParams & { configureAccess: false })
  | (BaseParams & {
      replicache: Replicache<typeof repoSpaceMutators> | undefined
      userId: string
      collapsePropertiesPanel: boolean
      configureAccess: true
      path: string
      editorMode: EditorMode
      editorStore: Pick<
        EditorState,
        'setSelectedBlockId' | 'setIsRenamingBlockId'
      >
      handleDelete: () => Promise<void>
      handleRemoveReferenceTarget: (() => void | Promise<void>) | null
      duplicateCallback?: (blockId: string) => void | Promise<void>
      urFocusOpStart: RepoUndoOps[number]
      urFocusOpEnd?: RepoUndoOps[number]
      handleRichTextConvertToPlainText: VoidFunction
    })

export const getBlockMenuActions = ({
  teamSlug,
  repoSlug,
  branchOrCommitSegment,
  parentBlockId,
  blocks,
  parentBlock,
  repoStore,
  tabsStore,
  treeManagerRef,
  undoManager,
  selectedVariants,
  ...adminProps
}: GetBlockMenuActionsParams): Array<MenuItem> => {
  if (!blocks.length) return []
  const [block] = blocks
  const isBulk = blocks.length > 1

  const actions: Array<MenuItem> = [
    {
      key: 'copy-link',
      type: 'action',
      icon: <Link2Icon width={16} height={16} />,
      label: 'Copy link to block',
      disabled: isBulk,
      onSelect: async () => {
        if (!treeManagerRef.current || !block) return
        let linkToBlock: URL | undefined = undefined
        if (isListBlock(parentBlock)) {
          linkToBlock = new URL(
            `/${teamSlug}/${repoSlug}/explore/${branchOrCommitSegment}/${idToSourceBlockId(
              block.id,
              treeManagerRef.current
            )}`,
            window.location.origin
          )
          linkToBlock.search = window.location.search
          navigator.clipboard.writeText(linkToBlock.toString())
        } else {
          linkToBlock = new URL(
            `/${teamSlug}/${repoSlug}/explore/${branchOrCommitSegment}/${idToSourceBlockId(
              parentBlockId,
              treeManagerRef.current
            )}`,
            window.location.origin
          )
          linkToBlock.search = window.location.search
          navigator.clipboard.writeText(
            linkToBlock.toString() +
              '#' +
              idToSourceBlockId(block.id, treeManagerRef.current)
          )
        }
        toast.success('Link copied to clipboard')
      },
    },
    {
      key: 'copy-block',
      type: 'action',
      icon: <ClipboardCopyIcon width={16} height={16} />,
      disabled: isBulk,
      label: 'Copy block',
      onSelect: async () => {
        if (!treeManagerRef.current || !block) return
        await copyBlockToClipboard({
          blockTreeManager: treeManagerRef.current,
          blockId: block.id,
          onSuccess: () => {
            toast.success(
              `"${block.title ?? 'Untitled'}" copied to clipboard`,
              {
                icon: <ClipboardCopyIcon width={16} height={16} />,
              }
            )
          },
          onError: (err) => {
            console.error(err)
            toast.error('Failed to copy block to clipboard')
          },
        })
      },
    },
    ...(block && isRichTextBlock(block)
      ? [
          {
            key: 'copy-as-markdown',
            type: 'action' as const,
            label: 'Copy as Markdown',
            disabled: isBulk,
            icon: <ClipboardCopyIcon width={16} height={16} />,
            onSelect: async () => {
              if (!treeManagerRef.current || !block) return

              const value = getValueBasedOnSelectedVariants({
                block,
                variantsToFilterBy: selectedVariants,
              })

              const rawHTML = tipTapJSONToHTMLString({
                isEmpty: !!value.state,
                json: stateToJson(value.state),
                extensions: richTextExtensionsForTransformations,
              })

              const enhancedHtml = await enhanceGeneratedHTML({
                html: rawHTML,
                options: { slugs: true, toc: undefined },
              })
              const file = await unified()
                .use(rehypeParse)
                .use(rehypeRemark)
                .use(remarkGfm)
                .use(remarkStringify)
                .process(enhancedHtml ?? '')

              await navigator.clipboard.writeText(String(file))
              toast.success('Markdown copied to clipboard')
            },
          },
        ]
      : []),
    ...(isInstanceBlock(parentBlock)
      ? [
          ...(() => {
            if (!block || isCommentBlock(block)) return []
            invariant(
              block.instanceChildTarget,
              'block.instanceChildTarget not found'
            )

            return [
              {
                type: 'action-link' as const,
                key: 'edit-in-component',
                icon: <GearIcon width={16} height={16} />,
                label: adminProps.configureAccess
                  ? 'Configure in component'
                  : 'Go to source component',
                href: `/${teamSlug}/${repoSlug}/explore/${branchOrCommitSegment}/${parentBlock.typeOptions.targetBlockId}#${block.instanceChildTarget}`,
                onSelect: async () => {
                  tabsStore.getState().addTab({
                    key: parentBlock.typeOptions.targetBlockId,
                    partiallyOpen: true,
                  })
                  repoStore.setFocus({
                    type: 'block:wrapper',
                    blockId: block.instanceChildTarget!,
                  })
                },
              },
            ]
          })(),
        ]
      : []),
    ...(block && isMediaBlock(block) && block.value.status === 'uploaded'
      ? [
          { type: 'separator' as const, key: 's-1' },
          {
            type: 'action' as const,
            key: 'copy-url',
            disabled: isBulk,
            icon: <ClipboardIcon width={16} height={16} />,
            label: 'Copy file URL',
            onSelect: async () => {
              if (block.value.status !== 'uploaded') {
                return toast.error(
                  'Please wait for the file to finish uploading before taking action.'
                )
              }
              navigator.clipboard.writeText(replaceAssetsURL(block.value.url))
              toast.success('URL copied to clipboard')
            },
          },
          {
            type: 'action' as const,
            icon: <DownloadIcon width={16} height={16} />,
            key: 'download-file',
            disabled: isBulk,
            label: 'Download file',
            onSelect: async () => {
              if (block.value.status !== 'uploaded') {
                return toast.error(
                  'Please wait for the file to finish uploading before taking action.'
                )
              }

              const fileName = block.value.fileName
              const minSleep = sleep(500)
              const downloadPromise = fetch(block.value.url).then(
                async (response) => {
                  if (!response.ok) {
                    throw new Error('Failed to download file')
                  }

                  const blob = await response.blob()
                  await minSleep

                  try {
                    // Use download attribute for better browser support
                    const a = document.createElement('a')
                    a.href = URL.createObjectURL(blob)
                    a.download = fileName
                    a.style.display = 'none'
                    document.body.appendChild(a)
                    a.click()

                    // Cleanup
                    setTimeout(() => {
                      document.body.removeChild(a)
                      URL.revokeObjectURL(a.href)
                    }, 100)
                  } catch (err) {
                    console.error('Download failed:', err)
                    throw new Error('Failed to initiate download')
                  }
                }
              )

              toast.promise(downloadPromise, {
                loading: 'Downloading...',
                error: 'Error downloading file',
                success: 'Download finished',
              })
            },
          },
        ]
      : []),
  ]

  if (!adminProps.configureAccess) return actions

  const canAlterSchema = adminProps.editorMode === 'write:all'
  const canAlterContent =
    adminProps.editorMode === 'write:all' ||
    adminProps.editorMode === 'write:value-only' ||
    adminProps.editorMode === 'write:value-and-title'

  if (
    adminProps.collapsePropertiesPanel ||
    canAlterSchema ||
    (canAlterContent &&
      (adminProps.editorMode === 'write:all' ||
        adminProps.editorMode === 'write:value-and-title'))
  ) {
    actions.push({ type: 'separator', key: 's-2' })
  }

  if (adminProps.collapsePropertiesPanel && block && canAlterContent) {
    actions.push({
      type: 'action' as const,
      key: 'expand-properties',
      icon: <GearIcon width={16} height={16} />,
      label: 'Properties',
      onSelect: async () => {
        await adminProps.replicache?.mutate.toggleCollapsePropertiesPanel(
          adminProps.userId
        )
        adminProps.editorStore.setSelectedBlockId(idToSourceBlockId(block))
      },
    })
  }

  if (canAlterContent) {
    if (canAlterSchema) {
      if (
        block &&
        (isListBlock(parentBlock) ||
          (!isDocumentBlock(block) &&
            !isInstanceBlock(block) &&
            !isComponentBlock(block)))
      ) {
        actions.push({
          type: 'action' as const,
          key: 'rename',
          icon: <Pencil1Icon width={16} height={16} />,
          disabled: isBulk,
          label: 'Rename',
          onSelect: () => {
            adminProps.editorStore.setIsRenamingBlockId(
              idToSourceBlockId(block)
            )
          },
        })
      }

      if (
        block &&
        isTextBlock(block) &&
        adminProps.editorMode === 'write:all'
      ) {
        actions.push({
          type: 'action',
          key: 'convert-to-rich-text',
          icon: (
            <BlockIcon
              block={{ type: 'rich-text' }}
              iconProps={{ width: 16, height: 16 }}
            />
          ),
          label: 'Convert to Rich Text',
          onSelect: async () => {
            const params: ChangeBlockTypeParams = {
              blockId: block.id,
              title: block.title,
              path: adminProps.path,
              isoNow: new Date().toISOString(),
              typeOptions: {},
              idSeed: getId(),
              newBlockType: blockTypesRecord.richText,
            }
            const result = await adminProps.replicache?.mutate
              .changeBlockType(params)
              .catch((err) => {
                if (err instanceof MutatorUserError) {
                  toast.error(`Failed to convert block: ${err.reason}`)
                } else {
                  console.error(err)
                  toast.error('Failed to convert block')
                }
              })

            if (result?.undo) {
              undoManager?.push({
                redo: [
                  ...(adminProps.urFocusOpStart
                    ? [adminProps.urFocusOpStart]
                    : []),
                  { op: 'changeBlockType', args: params },
                  ...(adminProps.urFocusOpEnd ? [adminProps.urFocusOpEnd] : []),
                ],
                undo: [
                  ...(adminProps.urFocusOpStart
                    ? [adminProps.urFocusOpStart]
                    : []),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                  ...(adminProps.urFocusOpEnd ? [adminProps.urFocusOpEnd] : []),
                ],
              })
            }

            adminProps.editorStore.setSelectedBlockId(idToSourceBlockId(block))
          },
        })
      }

      if (
        block &&
        isRichTextBlock(block) &&
        adminProps.editorMode === 'write:all'
      ) {
        actions.push({
          type: 'action',
          key: 'convert-to-plain-text',
          icon: (
            <BlockIcon
              block={{ type: 'text' }}
              iconProps={{ width: 16, height: 16 }}
            />
          ),
          label: 'Convert to Plain Text',
          onSelect: async (_meta) => {
            if (
              (_meta && 'confirmed' in _meta && _meta.confirmed) ||
              // is empty
              jsonToPlainText(
                stateToJson(block.value.state),
                richTextExtensionsForTransformations
              ).trim().length === 0
            ) {
              const params: ChangeBlockTypeParams = {
                blockId: block.id,
                title: block.title,
                path: adminProps.path,
                isoNow: new Date().toISOString(),
                typeOptions: {},
                idSeed: getId(),
                newBlockType: 'text',
              }
              const result = await adminProps.replicache?.mutate
                .changeBlockType(params)
                .catch((err) => {
                  if (err instanceof MutatorUserError) {
                    toast.error(`Failed to convert block: ${err.reason}`)
                  } else {
                    console.error(err)
                    toast.error('Failed to convert block')
                  }
                })

              if (result?.undo) {
                undoManager?.push({
                  redo: [
                    ...(adminProps.urFocusOpStart
                      ? [adminProps.urFocusOpStart]
                      : []),
                    { op: 'changeBlockType', args: params },
                    ...(adminProps.urFocusOpEnd
                      ? [adminProps.urFocusOpEnd]
                      : []),
                  ],
                  undo: [
                    ...(adminProps.urFocusOpStart
                      ? [adminProps.urFocusOpStart]
                      : []),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                    ...(adminProps.urFocusOpEnd
                      ? [adminProps.urFocusOpEnd]
                      : []),
                  ],
                })
              }
              return
            }

            adminProps.handleRichTextConvertToPlainText()
          },
        })
      }
    }

    const canUpdateHiddenState =
      !isInstanceBlock(parentBlock) &&
      (adminProps.editorMode === 'write:all' ||
        adminProps.editorMode === 'write:value-and-title')

    if (
      blocks.length &&
      blocks.every((b) => isHidableBlock(b)) &&
      canUpdateHiddenState &&
      canAlterContent
    ) {
      let canPerformActionOverAllBlocks = true
      const firstBlockHiddenState = blocks[0]?.hidden
      const allBlocksAreEitherHiddenOrVisible = blocks.every((b) => {
        if (
          shouldInheritHiddenState(
            b,
            adminProps.path + '/' + b.id,
            (id) => treeManagerRef.current?.getNode(id)?.value
          )
        ) {
          canPerformActionOverAllBlocks = false
          return !firstBlockHiddenState
        }
        return b.hidden === firstBlockHiddenState
      })
      let label = `${firstBlockHiddenState ? 'Show' : 'Hide'} block`

      if (blocks.length > 1) {
        if (allBlocksAreEitherHiddenOrVisible) {
          label = `${firstBlockHiddenState ? 'Show' : 'Hide'} ${
            blocks.length
          } blocks`
        } else {
          label = `Show ${blocks.length} blocks`
        }
      }

      actions.push({
        type: 'action' as const,
        key: 'hide-block',
        disabled: !canPerformActionOverAllBlocks,
        icon: firstBlockHiddenState ? (
          <EyeOpenIcon width={16} height={16} />
        ) : (
          <EyeClosedIcon width={16} height={16} />
        ),
        label,
        onSelect: async () => {
          if (!treeManagerRef.current) return

          await Promise.all(
            blocks.map(async (b) => {
              if (
                !treeManagerRef.current ||
                // because if some blocks are visible, we only want to show the hidden ones
                (!allBlocksAreEitherHiddenOrVisible && !b.hidden)
              ) {
                return
              }

              const params: UpdateBlockParams = {
                id: b.id,
                data: {
                  hidden: !b.hidden,
                  type: b.type,
                },
                instanceChildTarget: b.instanceChildTarget,
                skipPropagateHiddenState: !!b.hidden,
                strategy: 'merge',
                path: treeManagerRef.current.getPath(b.id, {
                  format: 'string',
                  required: true,
                }),
                isoNow: new Date().toISOString(),
              }

              const result =
                await adminProps?.replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager?.push({
                  redo: [
                    ...(adminProps.urFocusOpStart
                      ? [adminProps.urFocusOpStart]
                      : []),
                    { op: 'updateBlock', args: params },
                    ...(adminProps.urFocusOpEnd
                      ? [adminProps.urFocusOpEnd]
                      : []),
                  ],
                  undo: [
                    ...(adminProps.urFocusOpStart
                      ? [adminProps.urFocusOpStart]
                      : []),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                    ...(adminProps.urFocusOpEnd
                      ? [adminProps.urFocusOpEnd]
                      : []),
                  ],
                })
              }
            })
          )
        },
      })
    }

    if (
      canAlterSchema ||
      (adminProps.editorMode === 'write:value-and-title' &&
        block &&
        isInstanceBlock(block))
    ) {
      if (
        block &&
        (isInstanceBlock(block) || isComponentBlock(block)) &&
        !isListBlock(parentBlock) &&
        !isReferenceBlock(parentBlock)
      ) {
        actions.push(
          ...[
            {
              type: 'submenu' as const,
              key: 'wrap-in-collection',
              icon: <GridIcon width={16} height={16} />,
              disabled: isBulk,
              label: 'Wrap in collection',
              items: [
                {
                  type: 'action' as const,
                  key: 'wrap-in-table',
                  icon: (
                    <ListBlockViewIcon
                      view={{ layout: 'table' }}
                      iconProps={{ width: 16, height: 16 }}
                    />
                  ),
                  label: 'As table',
                  onSelect: async () => {
                    const params: ChangeBlockTypeParams = {
                      blockId: block.id,
                      title: block.title,
                      path: adminProps.path,
                      isoNow: new Date().toISOString(),
                      typeOptions: {},
                      layout: 'table',
                      idSeed: getId(),
                      newBlockType: blockTypesRecord.list,
                      columnIds: [idToSourceBlockId(block)],
                    }
                    const result =
                      await adminProps.replicache?.mutate.changeBlockType(
                        params
                      )
                    if (result?.undo) {
                      undoManager?.push({
                        redo: [
                          ...(adminProps.urFocusOpStart
                            ? [adminProps.urFocusOpStart]
                            : []),
                          { op: 'changeBlockType', args: params },
                          ...(adminProps.urFocusOpEnd
                            ? [adminProps.urFocusOpEnd]
                            : []),
                        ],
                        undo: [
                          ...(adminProps.urFocusOpStart
                            ? [adminProps.urFocusOpStart]
                            : []),
                          {
                            op: 'blockBatchMutations',
                            args: { mutations: result.undo },
                          },
                          ...(adminProps.urFocusOpEnd
                            ? [adminProps.urFocusOpEnd]
                            : []),
                        ],
                      })
                    }
                    adminProps.editorStore.setSelectedBlockId(
                      idToSourceBlockId(block)
                    )
                  },
                },
                {
                  type: 'action' as const,
                  key: 'wrap-in-gallery',
                  icon: (
                    <ListBlockViewIcon
                      view={{ layout: 'gallery' }}
                      iconProps={{ width: 16, height: 16 }}
                    />
                  ),
                  label: 'As gallery',
                  onSelect: async () => {
                    const params: ChangeBlockTypeParams = {
                      blockId: block.id,
                      title: block.title,
                      path: adminProps.path,
                      isoNow: new Date().toISOString(),
                      typeOptions: {},
                      layout: 'gallery',
                      idSeed: getId(),
                      newBlockType: blockTypesRecord.list,
                      columnIds: [idToSourceBlockId(block)],
                    }
                    const result =
                      await adminProps.replicache?.mutate.changeBlockType(
                        params
                      )
                    if (result?.undo) {
                      undoManager?.push({
                        redo: [
                          ...(adminProps.urFocusOpStart
                            ? [adminProps.urFocusOpStart]
                            : []),
                          { op: 'changeBlockType', args: params },
                          ...(adminProps.urFocusOpEnd
                            ? [adminProps.urFocusOpEnd]
                            : []),
                        ],
                        undo: [
                          ...(adminProps.urFocusOpStart
                            ? [adminProps.urFocusOpStart]
                            : []),
                          {
                            op: 'blockBatchMutations',
                            args: { mutations: result.undo },
                          },
                          ...(adminProps.urFocusOpEnd
                            ? [adminProps.urFocusOpEnd]
                            : []),
                        ],
                      })
                    }
                    adminProps.editorStore.setSelectedBlockId(
                      idToSourceBlockId(block)
                    )
                  },
                },
                {
                  type: 'action' as const,
                  key: 'wrap-in-list',
                  icon: (
                    <ListBlockViewIcon
                      iconProps={{ width: 16, height: 16 }}
                      view={{ layout: 'inline' }}
                    />
                  ),
                  label: 'As inline list',
                  onSelect: async () => {
                    const params: ChangeBlockTypeParams = {
                      blockId: block.id,
                      title: block.title,
                      path: adminProps.path,
                      isoNow: new Date().toISOString(),
                      typeOptions: {},
                      layout: 'inline',
                      idSeed: getId(),
                      newBlockType: blockTypesRecord.list,
                      columnIds: [idToSourceBlockId(block)],
                    }
                    const result =
                      await adminProps.replicache?.mutate.changeBlockType(
                        params
                      )
                    if (result?.undo) {
                      undoManager?.push({
                        redo: [
                          ...(adminProps.urFocusOpStart
                            ? [adminProps.urFocusOpStart]
                            : []),
                          { op: 'changeBlockType', args: params },
                          ...(adminProps.urFocusOpEnd
                            ? [adminProps.urFocusOpEnd]
                            : []),
                        ],
                        undo: [
                          ...(adminProps.urFocusOpStart
                            ? [adminProps.urFocusOpStart]
                            : []),
                          {
                            op: 'blockBatchMutations',
                            args: { mutations: result.undo },
                          },
                          ...(adminProps.urFocusOpEnd
                            ? [adminProps.urFocusOpEnd]
                            : []),
                        ],
                      })
                    }
                    adminProps.editorStore.setSelectedBlockId(
                      idToSourceBlockId(block)
                    )
                  },
                },
              ],
            },
          ]
        )
      }
    }

    if (
      adminProps.editorMode === 'write:value-and-title' ||
      adminProps.editorMode === 'write:all'
    ) {
      actions.push(
        ...([
          {
            type: 'action' as const,
            key: 'duplicate',
            disabled: isBulk,
            icon: <CopyIcon width={16} height={16} />,
            label: 'Duplicate',
            onSelect: async () => {
              if (isBulk || !block) return

              // If an image is being uploaded, wait for it to finish before duplicating.
              if (isMediaBlock(block) && block.value?.status === 'uploading') {
                return toast.error(
                  'Please wait for the file to finish uploading before duplicating the block.'
                )
              }

              const id = getId()
              const params: DuplicateBlockParams = {
                duplicatedBlockId: block.id,
                newBlockId: id,
                path: adminProps.path,
                isoNow: new Date().toISOString(),
                idSeed: getId(),
                duplicatedComponentStrategy: isComponentBlock(block)
                  ? 'create-instance'
                  : 'default',
              }
              const result =
                await adminProps.replicache?.mutate.duplicateBlock(params)
              if (!result) return

              undoManager?.push({
                redo: [
                  ...(adminProps.urFocusOpStart
                    ? [adminProps.urFocusOpStart]
                    : []),
                  { op: 'duplicateBlock', args: params },
                  ...(adminProps.urFocusOpEnd ? [adminProps.urFocusOpEnd] : []),
                ],
                undo: [
                  ...(adminProps.urFocusOpStart
                    ? [adminProps.urFocusOpStart]
                    : []),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                  ...(adminProps.urFocusOpEnd ? [adminProps.urFocusOpEnd] : []),
                ],
              })

              await adminProps.duplicateCallback?.(result.newBlock.id)
              repoStore.setFocus({
                type: 'block:name',
                blockId: idToSourceBlockId(result.newBlock),
              }) // focus shuold be at the start of this new block though.
            },
          },
          { type: 'separator', key: 's-3' },
          {
            type: 'action' as const,
            key: 'delete',
            icon: <TrashIcon width={16} height={16} />,
            label: 'Delete',
            onSelect: adminProps.handleDelete,
          },
        ] satisfies MenuItem[])
      )
    }
  } else if (
    adminProps.handleRemoveReferenceTarget &&
    isReferenceBlock(parentBlock)
  ) {
    // cannot alter content, so it's a referenced block

    actions.push(
      ...([
        {
          type: 'action' as const,
          key: 'duplicate',
          disabled: isBulk,
          icon: <CopyIcon width={16} height={16} />,
          label: 'Duplicate',
          onSelect: async () => {
            if (isBulk || !block) return

            // If an image is being uploaded, wait for it to finish before duplicating.
            if (isMediaBlock(block) && block.value?.status === 'uploading') {
              return toast.error(
                'Please wait for the file to finish uploading before duplicating the block.'
              )
            }

            const id = getId()
            const params: DuplicateBlockParams = {
              duplicatedBlockId: block.id,
              newBlockId: id,
              path: adminProps.path,
              isoNow: new Date().toISOString(),
              idSeed: getId(),
              duplicatedComponentStrategy: isComponentBlock(block)
                ? 'create-instance'
                : 'default',
            }
            const result =
              await adminProps.replicache?.mutate.duplicateBlock(params)
            if (!result) return

            undoManager?.push({
              redo: [
                ...(adminProps.urFocusOpStart
                  ? [adminProps.urFocusOpStart]
                  : []),
                { op: 'duplicateBlock', args: params },
                ...(adminProps.urFocusOpEnd ? [adminProps.urFocusOpEnd] : []),
              ],
              undo: [
                ...(adminProps.urFocusOpStart
                  ? [adminProps.urFocusOpStart]
                  : []),
                {
                  op: 'blockBatchMutations',
                  args: { mutations: result.undo },
                },
                ...(adminProps.urFocusOpEnd ? [adminProps.urFocusOpEnd] : []),
              ],
            })

            await adminProps.duplicateCallback?.(result.newBlock.id)
            repoStore.setFocus({
              type: 'block:name',
              blockId: idToSourceBlockId(result.newBlock),
            }) // focus shuold be at the start of this new block though.
          },
        },
        { type: 'separator', key: 's-3' },
        {
          type: 'action' as const,
          key: 'remove-reference',
          icon: <Cross2Icon width={16} height={16} />,
          label: 'Remove reference',
          onSelect: adminProps.handleRemoveReferenceTarget,
        },
      ] satisfies MenuItem[])
    )
  }
  return actions
}
